import React, { useState, useRef, useEffect } from 'react';
import { X, Send, MessageCircle, Loader2 } from 'lucide-react';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import axios from 'axios';

const GHOST_LOGO = "https://customer-assets.emergentagent.com/job_8a1d6c9e-dd92-45ef-96ba-a4a899e05fe9/artifacts/4lw5n36q_image.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GhostChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hello! I'm Ghost, your AI assistant from Prestigious Marketing. How can I help you today? Feel free to ask about our services, partnerships, or how we can help grow your business."
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId
      });

      if (response.data.success) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: response.data.response 
        }]);
        if (!sessionId) {
          setSessionId(response.data.session_id);
        }
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I apologize, but I'm having trouble connecting right now. Please try again in a moment, or contact us directly at info@prestigious-marketing.com" 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full bg-[#0F172A] shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 ${isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'}`}
        data-testid="ghost-chat-toggle"
        aria-label="Open Ghost Assistant"
      >
        <div className="absolute inset-0 rounded-full bg-[#1E3A8A] animate-pulse-ring opacity-50" />
        <img 
          src={GHOST_LOGO} 
          alt="Ghost Assistant" 
          className="w-10 h-10 object-contain ghost-float"
        />
      </button>

      {/* Chat Panel */}
      <div 
        className={`fixed bottom-6 right-6 z-50 w-[360px] sm:w-[400px] transition-all duration-300 transform ${
          isOpen 
            ? 'translate-y-0 opacity-100 scale-100' 
            : 'translate-y-4 opacity-0 scale-95 pointer-events-none'
        }`}
        data-testid="ghost-chat-panel"
      >
        <div className="glass rounded-2xl shadow-2xl overflow-hidden border border-slate-200/50">
          {/* Header */}
          <div className="bg-[#0F172A] px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center">
                <img 
                  src={GHOST_LOGO} 
                  alt="Ghost" 
                  className="w-7 h-7 object-contain"
                />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">Ghost Assistant</h3>
                <p className="text-slate-400 text-xs">Powered by Prestigious</p>
              </div>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
              data-testid="ghost-chat-close"
              aria-label="Close chat"
            >
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <ScrollArea className="h-[350px] p-4 bg-white">
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div 
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center mr-2 flex-shrink-0">
                      <img src={GHOST_LOGO} alt="" className="w-5 h-5" />
                    </div>
                  )}
                  <div 
                    className={`max-w-[80%] px-4 py-2.5 text-sm ${
                      msg.role === 'user' 
                        ? 'message-user' 
                        : 'message-ghost'
                    }`}
                  >
                    {msg.content}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center mr-2 flex-shrink-0">
                    <img src={GHOST_LOGO} alt="" className="w-5 h-5" />
                  </div>
                  <div className="message-ghost px-4 py-3 flex gap-1">
                    <div className="typing-dot" />
                    <div className="typing-dot" />
                    <div className="typing-dot" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Input */}
          <div className="p-4 bg-white border-t border-slate-100">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Ask Ghost anything..."
                className="flex-1 bg-slate-50 border-slate-200 focus:border-[#1E3A8A] focus:ring-[#1E3A8A]"
                disabled={isLoading}
                data-testid="ghost-chat-input"
              />
              <Button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="bg-[#1E3A8A] hover:bg-[#1e40af] px-3"
                data-testid="ghost-chat-send"
              >
                {isLoading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Send size={18} />
                )}
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-2 text-center">
              Ghost AI by Prestigious Marketing
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default GhostChat;
